import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  CREATE_TRAINER_ACTIVITY,
  DELETE_TRAINER_ACTIVITY,
  GET_TRAINER_ACTIVITY,
  UPDATE_TRAINER_ACTIVITY,
  UPSERT_TRAINER_ACTIVITY_API,
} from "../../Api";
import apiRequest from "../../Api/connector";
import { STATUS_200 } from "../../Api/constants";
import { SomethingWentWrong } from "../../utils/Constant";
import { showToast } from "../../utils/helper/helper";

// State interface
interface TrainerActivityState {
  activities: any[];
  activity: any;
  loading: boolean;
  error: string | null;
}

const initialState: TrainerActivityState = {
  activities: [],
  activity: null,
  loading: false,
  error: null,
};

export const createTrainerActivity = createAsyncThunk(
  CREATE_TRAINER_ACTIVITY,
  async (activityData: any) => {
    const response = await apiRequest(
      "POST",
      UPSERT_TRAINER_ACTIVITY_API(),
      activityData
    );
    return response;
  }
);

export const updateTrainerActivity = createAsyncThunk(
  UPDATE_TRAINER_ACTIVITY,
  async (activityData: any) => {
    const response = await apiRequest(
      "PUT",
      UPSERT_TRAINER_ACTIVITY_API(),
      activityData
    );
    return response;
  }
);

export const getTrainerActivities = createAsyncThunk(
  GET_TRAINER_ACTIVITY,
  async () => {
    const response = await apiRequest("GET", UPSERT_TRAINER_ACTIVITY_API());
    return response;
  }
);

export const deleteTrainerActivity = createAsyncThunk(
  DELETE_TRAINER_ACTIVITY,
  async (activityId: Number) => {
    const response = await apiRequest(
      "DELETE",
      UPSERT_TRAINER_ACTIVITY_API(activityId)
    );
    return response;
  }
);

const ActivityTrackingSlice = createSlice({
  name: "activityTracking",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // Create
      .addCase(createTrainerActivity.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTrainerActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.activity = action.payload;
        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(createTrainerActivity.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      })
      // Update
      .addCase(updateTrainerActivity.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrainerActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.activity = action.payload;
        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(updateTrainerActivity.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      })
      // Get
      .addCase(getTrainerActivities.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTrainerActivities.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload?.data?.activities)) {
          state.activities = action.payload.data.activities;
        } else if (action.payload?.data) {
          state.activity = action.payload.data;
        }
      })
      .addCase(getTrainerActivities.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      })
      // Delete
      .addCase(deleteTrainerActivity.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTrainerActivity.fulfilled, (state, action) => {
        state.loading = false;
        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(deleteTrainerActivity.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });
  },
});

export const actions = ActivityTrackingSlice.actions;
export default ActivityTrackingSlice.reducer;
